import React, { ReactNode, Suspense, useEffect, useState } from 'react';
import styled from 'styled-components';
import { defaultTheme, LinearProgress, ThemeProvider } from '@dls/react-core';
import './App.css';
import '@dls/react-fonts/index.css';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@material-ui/core';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';
import { setDefaultOptions } from 'date-fns';
import { RoutePaths } from './routes/main-routes';
import { Header } from './components/header/header';
import Algorithms from './pages/algorithms/algorithms';
import AddAlgorithm from './pages/algorithms/add-algorithm';
import { StudiesOverview, StudiesOverviewPanel } from './pages/studies/studies-overview';
import { SingleStudy } from './pages/studies/single-study';
import { CreateStudy } from './pages/studies/create-study';
import { SettingsOverview } from './pages/settings/settings-overview';
import { EditStudy } from './pages/studies/edit-study';
import { StudyInformation } from './pages/studies/edit-study/study-information';
import { StudyDevices } from './pages/studies/edit-study/study-devices';
import { Users } from './pages/studies/edit-study/users';
import { Subject } from './pages/subject';
import { SubjectDataOverview } from './pages/subject/data';
import { SubjectDevices } from './pages/subject/devices';
import { PersonalDetails } from './pages/subject/personal-details';
import { GlobalErrorBoundary } from './GlobalErrorBoundary';
import useRequest from './api/useRequest';
import { getSingleStudy, Study } from './api/studykit';
import { SWRConfig } from 'swr';
import { SnackbarProvider } from 'notistack';
import { Slide } from '@material-ui/core';
import { Configurations } from './pages/studies/edit-study/configurations';
import { ColorDLS } from './styling/color';
import { AuthorizationProvider } from './context/authorization';
// import { SubjectSummaryDashboard } from './pages/subject/summary';
import {
  fetchMFAPreference,
  getCurrentUser,
  setUpTOTP,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import './api/cognito';
import { LoginPage } from './pages/login/login-page';
import { ForgotPasswordPage } from './pages/forgot-password/forgot-password-page';
import { SetupTOTPForm, SetupTOTPFormFields } from './components/login/setup-totp-form';
import './pages/login/login-page.css';

import(`date-fns/locale/${navigator.languages[0]}/index.js`).then((locale) => {
  setDefaultOptions({ locale });
});

type AuthGuardState = 'loading' | 'authenticated' | 'mfa_required';

export const AuthGuard: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthGuardState>('loading');
  const [totpSetupDetails, setTotpSetupDetails] = useState<{
    sharedSecret: string;
    setupUri: string;
  } | null>(null);
  const [mfaError, setMfaError] = useState<string | undefined>();

  useEffect(() => {
    async function checkAuthAndMfa() {
      try {
        const user = await getCurrentUser();
        const mfaPreference = await fetchMFAPreference();
        const totpEnabled = mfaPreference.enabled?.includes('TOTP');
        const totpPreferred = mfaPreference.preferred === 'TOTP';

        if (!totpEnabled) {
          // User has never set up TOTP — show QR setup screen
          const setup = await setUpTOTP();
          setTotpSetupDetails({
            sharedSecret: setup.sharedSecret,
            setupUri: setup.getSetupUri('Studykit', user.username).toString(),
          });
          setState('mfa_required');
        } else {
          if (!totpPreferred) {
            // TOTP exists but not marked preferred — silently fix it
            await updateMFAPreference({ totp: 'PREFERRED' });
          }
          setState('authenticated');
        }
      } catch (e) {
        console.error('Error fetching current user:', e);
        window.location.href = '/login';
      }
    }

    checkAuthAndMfa();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmitMfaSetup = async (data: SetupTOTPFormFields) => {
    try {
      await verifyTOTPSetup({ code: data.code.toString() });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setMfaError(undefined);
      setState('authenticated');
    } catch (e) {
      setMfaError(e instanceof Error ? e.message : String(e));
    }
  };

  const onCancelMfaSetup = async () => {
    await signOut();
    window.location.href = '/login';
  };

  if (state === 'mfa_required' && totpSetupDetails) {
    return (
      <div className="login-container">
        <div className="login-logo philips-shield"></div>
        <div className="login-header">Studykit Login Page</div>
        <SetupTOTPForm
          onSubmit={onSubmitMfaSetup}
          onCancel={onCancelMfaSetup}
          totpSetupDetails={totpSetupDetails}
          error={mfaError}
        />
      </div>
    );
  }

  return state === 'authenticated' ? <>{children}</> : <></>;
};

export const StudyPageContext = React.createContext<{ study: Study }>(undefined as any);

const StudyPageContainer: React.FC = () => {
  const { studyId } = useParams();
  const { data } = useRequest(getSingleStudy(studyId!), { suspense: true });

  // Note: wrap again in authorization provider, so that it may check study-scoped permissions
  return (
    <StudyPageContext.Provider value={{ study: data! }}>
      <AuthorizationProvider>
        <Outlet />
      </AuthorizationProvider>
    </StudyPageContext.Provider>
  );
};

const MUI_THEME = createTheme({
  palette: {
    primary: {
      main: ColorDLS.Blue45,
    },
  },
  typography: {
    fontFamily: 'CentraleSans,Tahoma,Arial,Helvetica,sans-serif',
  },
});

function App() {
  return (
    <MuiThemeProvider theme={MUI_THEME}>
      <ThemeProvider theme={{ ...defaultTheme, accent: 'orange', tone: 'ultra-light' }}>
        <SnackbarProvider
          maxSnack={7}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          TransitionComponent={Slide}
        >
          <GlobalErrorBoundary>
            <Page>
              <BrowserRouter>
                <Routes>
                  <Route path={RoutePaths.Login} element={<LoginPage />} />
                  <Route path={RoutePaths.ForgotPassword} element={<ForgotPasswordPage />} />
                  <Route
                    element={
                      <AuthGuard>
                        <AuthorizationProvider>
                          <SWRConfig
                            value={{
                              refreshInterval: 1000 * 60,
                              revalidateIfStale: false,
                              revalidateOnFocus: true,
                              revalidateOnReconnect: true,
                              // onErrorRetry: async (
                              //   error,
                              //   _key,
                              //   _config,
                              //   revalidate,
                              //   { retryCount },
                              // ) => {
                              //   if (error.status === 404) return;
                              //   if (retryCount >= 3) return;
                              //   await AuthService.Instance.triggerRefreshFlow();
                              //   setTimeout(() => revalidate({ retryCount }), 3000);
                              // },
                            }}
                          >
                            <Header />
                            <Suspense fallback={<LinearProgress variant={'indeterminate'} />}>
                              <Outlet />
                            </Suspense>
                          </SWRConfig>
                        </AuthorizationProvider>
                      </AuthGuard>
                    }
                  >
                    <Route
                      path={RoutePaths.Default}
                      element={<Navigate to={RoutePaths.Studies} />}
                    />
                    <Route path={RoutePaths.Studies} element={<Outlet />}>
                      <Route index element={<Navigate to={'ongoing'} replace />} />
                      <Route element={<StudiesOverview />}>
                        <Route path={'ongoing'} element={<StudiesOverviewPanel />} />
                        <Route path={'completed'} element={<StudiesOverviewPanel />} />
                      </Route>
                    </Route>
                    <Route
                      path={`/${RoutePaths.Studies}/:studyId`}
                      element={<StudyPageContainer />}
                    >
                      <Route index element={<SingleStudy />} />
                      <Route path={`subjects/:subjectId`} element={<Outlet />}>
                        <Route index element={<Navigate to={'data'} replace />} />
                        <Route element={<Subject />}>
                          <Route path={'data'} element={<SubjectDataOverview />} />
                          {/* <Route path={'summary'} element={<SubjectSummaryDashboard />} /> */}
                          <Route path={'devices'} element={<SubjectDevices />} />
                          <Route path={'details'} element={<PersonalDetails />} />
                        </Route>
                      </Route>
                      <Route path={`edit`} element={<Outlet />}>
                        <Route index element={<Navigate to={'information'} replace />} />
                        <Route element={<EditStudy />}>
                          <Route path={'information'} element={<StudyInformation />} />
                          <Route path={'devices'} element={<StudyDevices />} />
                          <Route path={'users'} element={<Users />} />
                          <Route path={'configurations'} element={<Configurations />} />
                        </Route>
                      </Route>
                    </Route>
                    <Route path={RoutePaths.AddStudy} element={<CreateStudy />} />
                    <Route path={RoutePaths.Settings} element={<Outlet />}>
                      <Route path={RoutePaths.AddAlgorithm} element={<AddAlgorithm />} />
                      <Route
                        path={RoutePaths.EditAlgorithm + '/:algorithmId'}
                        element={<AddAlgorithm />}
                      />
                    </Route>
                    <Route path={RoutePaths.Settings} element={<SettingsOverview />}>
                      <Route index element={<Navigate to={RoutePaths.Algorithms} replace />} />
                      <Route path={RoutePaths.Algorithms} element={<Algorithms />} />
                    </Route>
                  </Route>
                </Routes>
              </BrowserRouter>
            </Page>
          </GlobalErrorBoundary>
        </SnackbarProvider>
      </ThemeProvider>
    </MuiThemeProvider>
  );
}

const Page = styled.div`
  > div {
    height: 100vh;
  }
`;

export default App;
